import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'

import { Badge } from '@/components/data-display/badge'
import { FormField } from '@/components/forms/form-field'
import { Select } from '@/components/forms/select'

import { listAdminOidcClients } from './api'
import type { EmbedOidcFormValues } from './oidc-form-values'

export function EmbedOidcBindingSection({
  onChange,
  value,
}: {
  onChange: (value: EmbedOidcFormValues) => void
  value: EmbedOidcFormValues
}) {
  const { t } = useTranslation('adminApps')
  const clientsQuery = useQuery({
    queryFn: listAdminOidcClients,
    queryKey: ['admin-idp-clients'],
  })
  const clients = clientsQuery.data ?? []
  const selected = clients.find((client) => client.id === value.clientRecordId)

  return (
    <section className="mt-5 grid gap-4 border-t border-[var(--color-panel-border)] pt-5">
      <div>
        <h3 className="text-sm font-semibold">{t('drawer.oidc.title')}</h3>
        <p className="mt-1 text-xs leading-5 text-[var(--color-text-muted)]">
          {t('drawer.oidc.bindingDescription')}
        </p>
      </div>
      <FormField
        hint={
          clientsQuery.isError
            ? t('drawer.oidc.clientsLoadFailed')
            : t('drawer.oidc.clientSelectionHint')
        }
        label={t('drawer.oidc.client')}
      >
        <Select
          disabled={clientsQuery.isLoading || clientsQuery.isError}
          onValueChange={(clientRecordId) => onChange({ clientRecordId })}
          options={[
            { label: t('drawer.oidc.noClient'), value: '' },
            ...clients.map((client) => ({
              description: client.client_id,
              label: client.name,
              value: client.id,
            })),
          ]}
          value={value.clientRecordId}
        />
      </FormField>
      {selected ? (
        <div className="flex flex-wrap items-center gap-2 text-xs text-[var(--color-text-muted)]">
          <Badge variant={selected.status === 'active' ? 'success' : 'neutral'}>
            {selected.status === 'active'
              ? t('drawer.oidc.clientActive')
              : t('drawer.oidc.clientInactive')}
          </Badge>
          <span>
            {t('drawer.oidc.selectedClientScopes', {
              scopes:
                selected.client_scope_assignments.map((scope) => scope.scope_name).join(', ') ||
                'openid',
            })}
          </span>
        </div>
      ) : null}
    </section>
  )
}
